import { FC, ReactNode, useMemo } from "react";

import { AuthTokenContext, AuthTokenContextValue } from "./AuthTokenContext";

export interface AuthTokenProviderProps {
  readonly authToken: string | null;
  readonly children: ReactNode;
}

/**
 * Provides an authentication token to child components via context.
 * This allows useRemoteTableDataSource and similar hooks to automatically
 * include Authorization headers without requiring explicit authToken props.
 *
 * This provider is defined in capitalrx-shell-auth (rather than capitalrx-components-2.0)
 * to ensure a single shared instance when used across the shell and federated modules.
 *
 * @see {@link AuthTokenContext} for the underlying context
 * @see {@link useAuthTokenContext} for the consumer hook
 *
 * @example
 * const [authToken] = useAuthToken();
 * return (
 *   <AuthTokenProvider authToken={authToken}>
 *     <App />
 *   </AuthTokenProvider>
 * );
 */
export const AuthTokenProvider: FC<AuthTokenProviderProps> = ({ authToken, children }) => {
  const value = useMemo<AuthTokenContextValue>(() => ({ authToken }), [authToken]);

  return <AuthTokenContext.Provider value={value}>{children}</AuthTokenContext.Provider>;
};
